import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Badge } from '../ui/badge';
import { Users, Calendar, CheckCircle2, X, ChevronDown } from 'lucide-react';
import api from '../../lib/api';

/**
 * TasksFilterBar — always-visible row above the Kanban / List.
 *
 * Three filters — decisions from Feb 2026:
 *   • Assignee (multi-select, employees only, populated via
 *     /employees?status=active + a "Me" quick-chip)
 *   • Due date range (from / to)
 *   • Completed date range (from / to) — mainly useful when you flip
 *     to List view and look at closed work
 *
 * All state is lifted to the parent so filter changes trigger a fetch
 * and drive the URL query string.
 */
export default function TasksFilterBar({
  currentUser,
  assigneeFilter, setAssigneeFilter,
  dueFrom, setDueFrom, dueTo, setDueTo,
  completedFrom, setCompletedFrom, completedTo, setCompletedTo,
}) {
  const [employees, setEmployees] = useState([]);
  const [empQuery, setEmpQuery] = useState('');

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const url = empQuery
          ? `/employees?status=active&search=${encodeURIComponent(empQuery)}&limit=30`
          : `/employees?status=active&limit=30`;
        const res = await api.get(url);
        setEmployees(res.data?.data || []);
      } catch {
        setEmployees([]);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [empQuery]);

  const toggleAssignee = (id) => {
    setAssigneeFilter(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectedNames = assigneeFilter.map(id => {
    if (currentUser?.id === id) return 'Me';
    const e = employees.find(x => x.id === id || x.user_id === id);
    if (!e) return id.slice(0, 6);
    return [e.first_name, e.last_name].filter(Boolean).join(' ') || e.email || id.slice(0, 6);
  });

  const activeCount = assigneeFilter.length
    + (dueFrom || dueTo ? 1 : 0)
    + (completedFrom || completedTo ? 1 : 0);

  const resetAll = () => {
    setAssigneeFilter([]);
    setDueFrom(''); setDueTo('');
    setCompletedFrom(''); setCompletedTo('');
  };

  return (
    <div className="flex flex-wrap items-center gap-2 py-2 px-1" data-testid="tasks-filter-bar">
      {/* Assignee multi-select */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="filter-assignee-btn">
            <Users className="h-3.5 w-3.5 mr-1.5" />
            {assigneeFilter.length === 0
              ? 'Assignee'
              : selectedNames.length <= 2
                ? selectedNames.join(', ')
                : `${selectedNames.slice(0, 2).join(', ')} +${selectedNames.length - 2}`}
            <ChevronDown className="h-3 w-3 ml-1.5 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <div className="p-2 border-b">
            <Input
              value={empQuery}
              onChange={(e) => setEmpQuery(e.target.value)}
              placeholder="Search employees…"
              className="h-8 text-sm"
              data-testid="filter-assignee-search"
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {/* "Me" quick-chip so a user can always filter to their own tasks. */}
            {currentUser?.id && (
              <button
                type="button"
                onClick={() => toggleAssignee(currentUser.id)}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-accent flex items-center justify-between ${assigneeFilter.includes(currentUser.id) ? 'bg-accent/60' : ''}`}
                data-testid="filter-assignee-me"
              >
                <span>👤 Me</span>
                {assigneeFilter.includes(currentUser.id) && (
                  <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                )}
              </button>
            )}
            {employees.map(e => {
              const uid = e.user_id || e.id;
              if (!uid || uid === currentUser?.id) return null;
              const name = [e.first_name, e.last_name].filter(Boolean).join(' ') || e.email;
              const selected = assigneeFilter.includes(uid);
              return (
                <button
                  key={uid}
                  type="button"
                  onClick={() => toggleAssignee(uid)}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-accent flex items-center justify-between ${selected ? 'bg-accent/60' : ''}`}
                  data-testid={`filter-assignee-opt-${uid}`}
                >
                  <span className="truncate">{name}</span>
                  {selected && <CheckCircle2 className="h-3.5 w-3.5 text-primary" />}
                </button>
              );
            })}
            {employees.length === 0 && (
              <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                No matches
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Due date range */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="filter-due-btn">
            <Calendar className="h-3.5 w-3.5 mr-1.5" />
            {dueFrom || dueTo
              ? `Due: ${dueFrom || '…'} → ${dueTo || '…'}`
              : 'Due date'}
            <ChevronDown className="h-3 w-3 ml-1.5 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3 space-y-2" align="start">
          <div>
            <label className="text-[11px] text-muted-foreground">From</label>
            <Input type="date" value={dueFrom} onChange={(e) => setDueFrom(e.target.value)}
                   className="h-8 text-sm" data-testid="filter-due-from" />
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground">To</label>
            <Input type="date" value={dueTo} onChange={(e) => setDueTo(e.target.value)}
                   className="h-8 text-sm" data-testid="filter-due-to" />
          </div>
          {(dueFrom || dueTo) && (
            <Button variant="ghost" size="sm" className="w-full h-7 text-xs"
                    onClick={() => { setDueFrom(''); setDueTo(''); }}>
              Clear due filter
            </Button>
          )}
        </PopoverContent>
      </Popover>

      {/* Completed date range */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="filter-completed-btn">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
            {completedFrom || completedTo
              ? `Done: ${completedFrom || '…'} → ${completedTo || '…'}`
              : 'Completed date'}
            <ChevronDown className="h-3 w-3 ml-1.5 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3 space-y-2" align="start">
          <div>
            <label className="text-[11px] text-muted-foreground">From</label>
            <Input type="date" value={completedFrom} onChange={(e) => setCompletedFrom(e.target.value)}
                   className="h-8 text-sm" data-testid="filter-completed-from" />
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground">To</label>
            <Input type="date" value={completedTo} onChange={(e) => setCompletedTo(e.target.value)}
                   className="h-8 text-sm" data-testid="filter-completed-to" />
          </div>
          {(completedFrom || completedTo) && (
            <Button variant="ghost" size="sm" className="w-full h-7 text-xs"
                    onClick={() => { setCompletedFrom(''); setCompletedTo(''); }}>
              Clear completed filter
            </Button>
          )}
        </PopoverContent>
      </Popover>

      {activeCount > 0 && (
        <>
          <Badge variant="secondary" className="text-[10px]">{activeCount} active</Badge>
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={resetAll} data-testid="filter-reset">
            <X className="h-3.5 w-3.5 mr-1" /> Reset
          </Button>
        </>
      )}
    </div>
  );
}
